import { Popover, Portal, usePopoverContext } from "@chakra-ui/react"
import { useEffect, useRef } from "react"
import {
  FULLSCREEN_LAYER_BASE,
  FULLSCREEN_LAYER_OVERLAY
} from "../fullscreen/Fullscreen"

function toAnchorDOMRect(rect) {
  if (!rect) return null
  return DOMRect.fromRect({
    x: rect.left,
    y: rect.top,
    width: Math.max(rect.width || 0, 1),
    height: Math.max(rect.height || 0, 1)
  })
}

/** Calls popover.reposition() whenever the caret rect moves. */
function RepositionOnRectChange({ rect }) {
  const { reposition } = usePopoverContext()

  useEffect(() => {
    if (!rect) return
    reposition()
  }, [
    rect?.top,
    rect?.left,
    rect?.bottom,
    rect?.right,
    rect?.width,
    rect?.height,
    reposition
  ])

  return null
}

/**
 * Anchors a Chakra Popover to a caret/selection rect (from useCursorRect / Quill bounds).
 * Visibility is controlled by whether this component is mounted.
 */
const VariableSelectorPicker = ({ rect, children }) => {
  const rectRef = useRef(rect)
  rectRef.current = rect

  if (!rect) return null

  return (
    <Popover.Root
      open
      autoFocus={false}
      closeOnEscape={false}
      closeOnInteractOutside={false}
      lazyMount
      unmountOnExit
      positioning={{
        placement: "bottom-center",
        gutter: 8,
        flip: false,
        overflowPadding: 10,
        getAnchorRect: () => toAnchorDOMRect(rectRef.current)
      }}
    >
      <Portal>
        <Popover.Positioner
          zIndex={FULLSCREEN_LAYER_BASE + FULLSCREEN_LAYER_OVERLAY}
        >
          <Popover.Content
            css={{
              "--popover-bg": "black",
              "--popover-size": "min(200px, calc(100vw - 20px))"
            }}
            color="white"
            borderWidth={0}
            p={2}
            borderRadius="card"
            _focusVisible={{ outline: "none" }}
          >
            <Popover.Arrow />
            <Popover.Body p={0} overflowY="auto">
              <RepositionOnRectChange rect={rect} />
              {children}
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  )
}

VariableSelectorPicker.displayName = "VariableSelectorPicker"

export default VariableSelectorPicker
